import { PortfolioItem, PortfolioImage } from '@/hooks/useProfile';
import { normalizePortfolioItem } from '@/hooks/portfolio/usePortfolioNormalizer';

interface PortfolioImagesHookReturn {
  handleAddImage: (projectId: string, imageUrl: string) => void;
  handleRemoveImage: (projectId: string, imageId: string) => void;
  handleReorderImages: (projectId: string, imageIds: string[]) => void;
  handleMoveImage: (projectId: string, imageId: string, direction: 'up' | 'down') => void;
  handleUpdateImageCaption: (projectId: string, imageId: string, caption: string) => void;
}

// Enhanced URL validation function with HTTPS requirement
const isValidImageUrl = (url: string): boolean => {
  if (!url) return false;
  
  try {
    // Accept data:image URLs for previews
    if (url.startsWith('data:image/')) {
      return true;
    }
    
    // Enforce HTTPS protocol (allow HTTP for local development)
    const isValidProtocol = url.startsWith('https://') || 
      (process.env.NODE_ENV === 'development' && url.startsWith('http://'));
    
    return isValidProtocol;
  } catch (e) {
    console.warn('Invalid image URL format:', url, e);
    return false;
  }
};

export function usePortfolioImages(
  initialItems: PortfolioItem[] | null,
  onPortfolioChange: (portfolioItems: PortfolioItem[]) => void
): PortfolioImagesHookReturn {
  const handleAddImage = (projectId: string, imageUrl: string) => {
    if (!initialItems) return;
    
    // Validate image URL before adding
    if (!isValidImageUrl(imageUrl)) {
      console.warn("Invalid image URL format:", imageUrl);
      return;
    }
    
    const itemIndex = initialItems.findIndex(item => item.id === projectId);
    if (itemIndex === -1) return;
    
    // Get a normalized copy of the item
    const project = normalizePortfolioItem(initialItems[itemIndex]);
    const updatedImages = [...(project.images || [])];
    
    if (updatedImages.length >= 20) {
      console.warn("Maximum number of images (20) reached for this project");
      return;
    }
    
    const newImage: PortfolioImage = {
      id: crypto.randomUUID(),
      url: imageUrl,
      order: updatedImages.length,
      caption: '',
      isLegacy: false
    };
    
    updatedImages.push(newImage);
    
    const updatedProject = {
      ...project,
      images: updatedImages,
      image_url: updatedImages[0].url // Set first image as main image
    };
    
    const updatedItems = [...initialItems];
    updatedItems[itemIndex] = updatedProject;
    
    onPortfolioChange(updatedItems);
  };

  const handleRemoveImage = (projectId: string, imageId: string) => {
    if (!initialItems) return;
    
    const itemIndex = initialItems.findIndex(item => item.id === projectId);
    if (itemIndex === -1) return;
    
    // Get a normalized copy of the item
    const project = normalizePortfolioItem(initialItems[itemIndex]);
    const updatedImages = (project.images || []).filter(img => img.id !== imageId);
    
    // Reorder remaining images
    updatedImages.forEach((img, idx) => {
      img.order = idx;
    });
    
    const updatedProject = {
      ...project,
      images: updatedImages,
      image_url: updatedImages.length > 0 ? updatedImages[0].url : null
    };
    
    const updatedItems = [...initialItems];
    updatedItems[itemIndex] = updatedProject;
    
    onPortfolioChange(updatedItems);
  };

  const handleReorderImages = (projectId: string, imageIds: string[]) => {
    if (!initialItems) return;
    
    const itemIndex = initialItems.findIndex(item => item.id === projectId);
    if (itemIndex === -1) return;
    
    // Get a normalized copy of the item
    const project = normalizePortfolioItem(initialItems[itemIndex]);
    if (!project.images) return;
    
    const imageMap = new Map(project.images.map(img => [img.id, img]));
    
    const reorderedImages = imageIds.map((id, index) => {
      const image = imageMap.get(id);
      if (!image) return null;
      
      return {
        ...image,
        order: index
      };
    }).filter(Boolean) as PortfolioImage[];
    
    const updatedProject = {
      ...project,
      images: reorderedImages,
      image_url: reorderedImages.length > 0 ? reorderedImages[0].url : null
    };
    
    const updatedItems = [...initialItems];
    updatedItems[itemIndex] = updatedProject;
    
    onPortfolioChange(updatedItems);
  };

  const handleMoveImage = (projectId: string, imageId: string, direction: 'up' | 'down') => {
    if (!initialItems) return;
    
    const itemIndex = initialItems.findIndex(item => item.id === projectId);
    if (itemIndex === -1) return;
    
    // Get a normalized copy of the item
    const project = normalizePortfolioItem(initialItems[itemIndex]);
    if (!project.images) return;
    
    const images = [...project.images];
    const imageIndex = images.findIndex(img => img.id === imageId);
    
    if (imageIndex === -1) return;
    if (direction === 'up' && imageIndex === 0) return;
    if (direction === 'down' && imageIndex === images.length - 1) return;
    
    const newIndex = direction === 'up' ? imageIndex - 1 : imageIndex + 1;
    
    // Swap order values
    const temp = images[imageIndex].order;
    images[imageIndex].order = images[newIndex].order;
    images[newIndex].order = temp;
    
    // Sort by order
    images.sort((a, b) => (a.order || 0) - (b.order || 0));
    
    const updatedProject = {
      ...project,
      images: images,
      image_url: images[0].url // First image after sort is the main image
    };
    
    const updatedItems = [...initialItems];
    updatedItems[itemIndex] = updatedProject;
    
    onPortfolioChange(updatedItems);
  };

  const handleUpdateImageCaption = (projectId: string, imageId: string, caption: string) => {
    if (!initialItems) return;
    
    const itemIndex = initialItems.findIndex(item => item.id === projectId);
    if (itemIndex === -1) return;
    
    // Get a normalized copy of the item
    const project = normalizePortfolioItem(initialItems[itemIndex]);
    if (!project.images) return;
    
    const updatedImages = project.images.map(img => {
      if (img.id === imageId) {
        return { ...img, caption };
      }
      return img;
    });
    
    const updatedProject = {
      ...project,
      images: updatedImages
    };
    
    const updatedItems = [...initialItems];
    updatedItems[itemIndex] = updatedProject;
    
    onPortfolioChange(updatedItems);
  };

  return {
    handleAddImage,
    handleRemoveImage,
    handleReorderImages,
    handleMoveImage,
    handleUpdateImageCaption
  };
}
